import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { useAddAuthContact, useRemoveAuthContact, useDatasheetAuthContacts } from "@/lib/datasheets";

export function AuthContactsSection({ datasheetId }: { datasheetId: number }) {
  const { data, isLoading } = useDatasheetAuthContacts(datasheetId);
  const { has } = usePermissions();
  const add = useAddAuthContact(datasheetId);
  const remove = useRemoveAuthContact(datasheetId);
  const [picked, setPicked] = useState<ClientPickerValue | null>(null);
  const canWrite = has("datasheet:write");

  async function addContact() {
    if (!picked) return;
    try {
      await add.mutateAsync(picked.clientId);
      setPicked(null);
      toast.success("Contact added");
    } catch (e) {
      const dup = e instanceof ApiError && e.status === 409;
      toast.error(dup ? "That contact is already authorised." : "Could not add the contact");
    }
  }

  async function removeContact(clientId: number) {
    try {
      await remove.mutateAsync(clientId);
      toast.success("Contact removed");
    } catch {
      toast.error("Could not remove the contact");
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <>
      {!data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No authorised contacts.</p>
      ) : (
        <ul className="text-sm">
          {data.map((c) => (
            <li key={c.datasheetAuthContactId} className="flex items-center justify-between border-b py-2 last:border-0">
              <span>
                <span className="font-medium">{c.clientName ?? `Client #${c.clientId}`}</span>
                {c.emailAddress ? <span className="text-muted-foreground"> · {c.emailAddress}</span> : null}
              </span>
              {canWrite ? (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove ${c.clientName ?? "contact"}`}
                  onClick={() => void removeContact(c.clientId)}
                >
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canWrite ? (
        <div className="mt-3 flex items-center gap-2">
          <ClientPicker value={picked} onChange={setPicked} placeholder="Add a contact…" />
          <Button size="sm" onClick={() => void addContact()} disabled={!picked || add.isPending}>
            Add
          </Button>
        </div>
      ) : null}
    </>
  );
}
